/* stackLaw.js — "a unit goes ABOVE another one when there is space" (batch 9, 2026-09-28).
 *
 * User: "the stock unit needs to go above the wall unit without a problem if there is
 * space — add a law and fix it." A drop that lands on an OCCUPIED span (a base run, a wall
 * unit, a tall) used to be refused or slid sideways. The law: try the tops of every unit the
 * candidate overlaps horizontally, lowest first; the first level where the unit fits with no
 * vertical overlap and its top under the ceiling wins, and the unit is lifted there through
 * config.position.elevation (mm above its role's base line — the same field the Edit
 * Position panel edits, so it stays editable). Nothing else moves. */
import { lhcVSpan } from "./placement";
import { roleBaseY } from "./rules";
import { layerCollides } from "./collision";
import { panelSpanOf } from "./sidePanelClearance";

const EPS = 0.002;

function wallHeightOf(state, wallId) {
  const w = (state.walls || []).find((x) => x.id === wallId);
  if (w && Number(w.height) > 0) return Number(w.height);
  const kw = state.kitchenWizard || {};
  return Number(kw.wallHeight) > 0 ? Number(kw.wallHeight) / 1000 : 2.7;
}

/** stackElevationFor(cand, def, state, lib) → { elevationMm, overTop } for the lowest level
 *  the unit can sit on, or null when it already fits where it is / nothing fits. `cand` is
 *  the placement to test (absLeft + wallId set, config optional). */
const HUNG = new Set(["cabinet-wall", "cabinet-stack", "cabinet-bridge"]);
/** only HUNG units stack (a wall unit / stack box over a wall unit or a tall); a floor unit
 *  dropped on an occupied span keeps the old behaviour (slide beside, or refuse) — a base
 *  cabinet never climbs onto another base cabinet */
export function stackElevationFor(cand, def, state, lib) {
  if (!cand || !def || !cand.wallId || cand.island) return null;
  if (!HUNG.has(def.role)) return null;
  const effOf = (p) => (lib && lib[p.type]) || null;
  const sp = panelSpanOf(cand, def);
  const c0 = cand.absLeft + sp.lo, c1 = cand.absLeft + sp.hi;
  // the units this one overlaps along the wall (same wall, same face)
  const fs = (p) => (p.faceSign === -1 ? -1 : 1);
  const over = (state.placements || []).filter((q) => {
    if (!q || q.id === cand.id || q.wallId !== cand.wallId || q.island) return false;
    if (fs(q) !== fs(cand)) return false;
    const qd = effOf(q); if (!qd) return false;
    if (!layerCollides(def.role, qd.role)) return false;
    const qs = panelSpanOf(q, qd);
    const q0 = q.absLeft + qs.lo, q1 = q.absLeft + qs.hi;
    return Math.min(c1, q1) - Math.max(c0, q0) > EPS;
  });
  if (!over.length) return null;
  const spans = over.map((q) => lhcVSpan(q, effOf(q)));
  const [b0, t0] = lhcVSpan(cand, def);
  const h = t0 - b0;
  const collides = (bot, top) => spans.some(([qb, qt]) => bot < qt - EPS && top > qb + EPS);
  if (!collides(b0, t0)) return null;                       // already free where it is
  const ceiling = wallHeightOf(state, cand.wallId);
  const base = roleBaseY(def);
  const tops = [...new Set(spans.map(([, qt]) => Math.round(qt * 1000) / 1000))].sort((a, b) => a - b);
  for (const top of tops) {
    if (top + h > ceiling + EPS) break;                      // no room above this one (or any higher)
    if (!collides(top, top + h)) {
      const elevationMm = Math.round((top - base) * 1000);
      if (elevationMm < 0) continue;                         // below its own base line: not a stack
      return { elevationMm, overTop: top };
    }
  }
  return null;
}

/** applyStack(cand, res): write the elevation into the candidate's config (mutates). */
export function applyStack(cand, res) {
  if (!cand || !res) return false;
  cand.config = cand.config || {};
  cand.config.position = { ...(cand.config.position || {}), elevation: res.elevationMm };
  return true;
}
